import { PlaylistItem } from "../../../../APITypes/PlaylistItem.cs";
import { cn } from "@/lib/utils";
import { segment } from "../../../../APITypes/Segment.cs";
import { useDataContext } from "../../../../app/_context";

export default function PlaybackSettings(props: {
  widgetMode: boolean;
  highlightsArray: segment[];
  playList: PlaylistItem[];
}) {
  const { isHighlightsOnlyMode, setIsHighlightsOnlyMode, currentPlaylist } =
    useDataContext();

  if (currentPlaylist.length <= 1) {
    return null;
  }

  const hasHighlights = props.playList.some(
    (p: PlaylistItem) => p.highlights.length > 0,
  );

  return (
    <div
      id="Base_div"
      className={cn(
        "mx-auto w-full border-t-2 border-background bg-stone-200 dark:bg-[#1e262fCD]",
        "flex justify-center items-stretch gap-1 p-1 md: p-2 ",
      )}
    >
      <PlaybackOption
        title="Full research"
        subtitle="Screen and commentary"
        selected={!isHighlightsOnlyMode}
        onSelect={() => setIsHighlightsOnlyMode(false)}
        bar={<SolidBar />}
      />

      {hasHighlights && (
        <PlaybackOption
          title="Highlights only"
          subtitle="Exclusively commentary only"
          selected={isHighlightsOnlyMode}
          onSelect={() => setIsHighlightsOnlyMode(true)}
          bar={<SegmentedBar segments={props.highlightsArray ?? []} />}
        />
      )}
    </div>
  );
}

function PlaybackOption(props: {
  title: string;
  subtitle: string;
  selected: boolean;
  onSelect: () => void;
  bar: React.ReactNode;
}) {
  const { title, subtitle, selected, onSelect, bar } = props;
  return (
    <button
      type="button"
      onClick={onSelect}
      aria-pressed={selected}
      className={cn(
        "flex-1 max-w-[320px] text-left rounded-2xl border-2 px-4 py-3 transition-colors duration-200",
        "flex flex-col justify-between gap-3",
        selected
          ? "bg-[#FBF5E8] border-yellow-600 dark:bg-stone-800 dark:border-yellow-500"
          : "bg-white border-stone-300 hover:border-stone-400 dark:bg-stone-900 dark:border-stone-700",
      )}
    >
      <div className="flex items-start justify-between gap-3">
        <div>
          <p
            className={cn(
              "text-base font-semibold leading-tight",
              selected
                ? "text-yellow-700 dark:text-yellow-500"
                : "text-stone-900 dark:text-stone-100",
            )}
          >
            {title}
          </p>
          {/* <p
            className={cn(
              "hidden md:block",
              "text-xs mt-0.5",
              selected
                ? "text-stone-600 dark:text-stone-300"
                : "text-stone-500 dark:text-stone-400",
            )}
          >
            {subtitle}
          </p> */}
        </div>
        <RadioDot selected={selected} />
      </div>

      {/* <div className="mt-1">{bar}</div> */}
    </button>
  );
}

function RadioDot({ selected }: { selected: boolean }) {
  return (
    <span
      className={cn(
        "md:inline-flex shrink-0 hidden items-center justify-center w-5 h-5 rounded-full border-2",
        selected
          ? "border-yellow-600 dark:border-yellow-500"
          : "border-stone-400 dark:border-stone-500",
      )}
    >
      {selected && (
        <span className="block w-2.5 h-2.5 rounded-full bg-yellow-600 dark:bg-yellow-500" />
      )}
    </span>
  );
}

function SolidBar() {
  return (
    <div className="h-1.5 w-full rounded-full bg-yellow-600 dark:bg-yellow-500" />
  );
}

function SegmentedBar({ segments }: { segments: segment[] }) {
  const count = Math.max(1, Math.min(segments.length || 4, 6));
  const total = segments.reduce((acc, s) => acc + (s.length || 0), 0);
  const widths =
    total > 0
      ? segments
          .slice(0, count)
          .map((s) => `${Math.max(8, ((s.length || 0) / total) * 100)}%`)
      : Array.from({ length: count }, () => `${100 / count}%`);

  return (
    <div className="flex w-full gap-1.5">
      {widths.map((w, i) => (
        <div
          key={i}
          style={{ width: w }}
          className="h-1.5 rounded-full bg-stone-300 dark:bg-stone-600"
        />
      ))}
    </div>
  );
}
